<template>
  <div class="login max-w-md mx-auto">
    <h1 class="text-3xl font-bold mb-8 text-primary dark:text-primary-light">Login</h1>
    
    <!-- Email Login Form (Primary Method) -->
    <div v-if="!showPasswordLogin && !showTokenLogin && !isProcessingToken && mfaStage === 'none'" class="card p-8">
      <div class="mb-6">
        <label class="block text-text-secondary dark:text-text-secondary-dark text-sm font-bold mb-2" for="email">
          Email Address
        </label>
        <input 
          v-model="email" 
          class="w-full px-3 py-2 bg-background-primary dark:bg-background-primary-dark border border-border dark:border-border-dark rounded-md text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light focus:border-transparent" 
          id="email" 
          type="email" 
          placeholder="Enter your email address" 
          required
          :disabled="isLoading"
        >
      </div>
      <div class="flex items-center justify-between mb-6">
        <button 
          class="btn-primary w-full relative" 
          type="button"
          @click="handleEmailLogin"
          :disabled="isLoading || !email"
        >
          <span v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          <span :class="{ 'opacity-0': isLoading }">Send Login Token</span>
        </button>
      </div>
      <div class="text-center space-y-2">
        <button 
          @click="showPasswordLogin = true"
          class="text-primary dark:text-primary-light hover:underline text-sm block"
          :disabled="isLoading"
        >
          Login with password instead
        </button>
        <button 
          @click="showTokenLogin = true"
          class="text-primary dark:text-primary-light hover:underline text-sm block"
          :disabled="isLoading"
        >
          I have a login token
        </button>
      </div>
    </div>

    <!-- Token Login Form -->
    <div v-if="showTokenLogin && mfaStage === 'none' && !isProcessingToken" class="card p-8">
      <!-- Success message when coming from email -->
      <div v-if="email" class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
        <p class="text-green-700 dark:text-green-300 text-sm">
          ✅ Login token sent to <strong>{{ email }}</strong>. Check your email and enter the token below.
        </p>
      </div>
      
      <div class="mb-6">
        <label class="block text-text-secondary dark:text-text-secondary-dark text-sm font-bold mb-2" for="token">
          Login Token
        </label>
        <input 
          v-model="loginToken" 
          class="w-full px-3 py-2 bg-background-primary dark:bg-background-primary-dark border border-border dark:border-border-dark rounded-md text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light focus:border-transparent font-mono text-sm" 
          id="token" 
          type="text" 
          placeholder="Enter the token from your email" 
          required
          :disabled="isLoading"
        >
      </div>
      <div class="flex items-center justify-between mb-6">
        <button 
          class="btn-primary w-full relative" 
          type="button"
          @click="handleTokenLogin"
          :disabled="isLoading || !loginToken"
        >
          <span v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          <span :class="{ 'opacity-0': isLoading }">Sign In with Token</span>
        </button>
      </div>
      <div class="text-center">
        <button 
          @click="showTokenLogin = false"
          class="text-primary dark:text-primary-light hover:underline text-sm"
          :disabled="isLoading"
        >
          Back to email login
        </button>
      </div>
    </div>

    <!-- MFA code prompt (shown after a valid password when user has TOTP enabled) -->
    <div v-if="mfaStage === 'code' && !isProcessingToken" class="card p-8">
      <h2 class="text-lg font-semibold mb-2 text-text-primary dark:text-text-primary-dark">Two-factor code</h2>
      <p class="text-sm text-text-secondary dark:text-text-secondary-dark mb-4">
        Enter the 6-digit code from your authenticator app.
      </p>
      <input
        v-model="mfaCode"
        maxlength="6"
        inputmode="numeric"
        pattern="[0-9]{6}"
        autocomplete="one-time-code"
        class="w-full px-3 py-2 bg-background-primary dark:bg-background-primary-dark border border-border dark:border-border-dark rounded-md text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light tracking-[0.4em] text-center text-lg font-mono"
        placeholder="123456"
        @keyup.enter="submitMfaCode"
      />
      <button
        class="btn-primary w-full mt-4"
        type="button"
        :disabled="isLoading || mfaCode.length !== 6"
        @click="submitMfaCode"
      >Verify</button>
      <button
        class="text-primary dark:text-primary-light hover:underline text-sm block mx-auto mt-4"
        @click="cancelMfa"
      >Cancel</button>
    </div>

    <!-- MFA enrolment (admin has required 2FA but user hasn't set it up yet) -->
    <div v-if="mfaStage === 'enroll' && !isProcessingToken" class="card p-8">
      <h2 class="text-lg font-semibold mb-2 text-text-primary dark:text-text-primary-dark">Set up two-factor authentication</h2>
      <p class="text-sm text-text-secondary dark:text-text-secondary-dark mb-4">
        Your administrator requires 2FA on this account. Scan the QR code with an authenticator app (Google Authenticator, 1Password, Authy…) and enter the current 6-digit code to finish signing in.
      </p>
      <div v-if="mfaSetup?.qr_code" class="flex justify-center mb-4">
        <img :src="mfaSetup.qr_code" alt="TOTP QR code" class="w-48 h-48 border border-border dark:border-border-dark rounded-md bg-white" />
      </div>
      <details v-if="mfaSetup?.secret" class="mb-4 text-sm">
        <summary class="cursor-pointer text-text-secondary dark:text-text-secondary-dark">Can't scan? Enter the secret manually</summary>
        <code class="block mt-2 p-2 bg-background-primary dark:bg-background-primary-dark border border-border dark:border-border-dark rounded font-mono break-all">{{ mfaSetup.secret }}</code>
      </details>
      <input
        v-model="mfaCode"
        maxlength="6"
        inputmode="numeric"
        pattern="[0-9]{6}"
        autocomplete="one-time-code"
        class="w-full px-3 py-2 bg-background-primary dark:bg-background-primary-dark border border-border dark:border-border-dark rounded-md text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light tracking-[0.4em] text-center text-lg font-mono"
        placeholder="123456"
        @keyup.enter="confirmMfaEnrollment"
      />
      <button
        class="btn-primary w-full mt-4"
        type="button"
        :disabled="isLoading || mfaCode.length !== 6"
        @click="confirmMfaEnrollment"
      >Verify &amp; sign in</button>
      <button
        class="text-primary dark:text-primary-light hover:underline text-sm block mx-auto mt-4"
        @click="cancelMfa"
      >Cancel</button>
    </div>

    <!-- Password Login Form (Secondary Method) -->
    <div v-if="showPasswordLogin && mfaStage === 'none' && !isProcessingToken" class="card p-8">
      <div class="mb-4">
        <label class="block text-text-secondary dark:text-text-secondary-dark text-sm font-bold mb-2" for="password-email">
          Email
        </label>
        <input 
          v-model="passwordEmail" 
          class="w-full px-3 py-2 bg-background-primary dark:bg-background-primary-dark border border-border dark:border-border-dark rounded-md text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light focus:border-transparent" 
          id="password-email" 
          type="email" 
          placeholder="Email" 
          required
          :disabled="isLoading"
        >
      </div>
      <div class="mb-6">
        <label class="block text-text-secondary dark:text-text-secondary-dark text-sm font-bold mb-2" for="password">
          Password
        </label>
        <input 
          v-model="password" 
          class="w-full px-3 py-2 bg-background-primary dark:bg-background-primary-dark border border-border dark:border-border-dark rounded-md text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-light focus:border-transparent" 
          id="password" 
          type="password" 
          placeholder="******************" 
          required
          :disabled="isLoading"
        >
      </div>
      <div class="flex items-center justify-between mb-6">
        <button 
          class="btn-primary w-full relative" 
          type="button"
          @click="handlePasswordLogin"
          :disabled="isLoading || !passwordEmail || !password"
        >
          <span v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
          <span :class="{ 'opacity-0': isLoading }">Sign In</span>
        </button>
      </div>
      <div class="text-center space-y-2">
        <router-link to="/forgot-password" class="text-primary dark:text-primary-light hover:underline text-sm block">
          Forgot password?
        </router-link>
        <button
          type="button"
          @click="startSSO"
          class="text-primary dark:text-primary-light hover:underline text-sm block mx-auto"
          :disabled="isLoading || !passwordEmail"
        >
          Sign in with SSO (OIDC)
        </button>
        <button 
          @click="showPasswordLogin = false"
          class="text-primary dark:text-primary-light hover:underline text-sm"
          :disabled="isLoading"
        >
          Back to email login
        </button>
      </div>
    </div>

    <!-- SSO tenant picker (only shown when the user has OIDC in more than one tenant) -->
    <div v-if="ssoPicker.length > 1" class="card p-8">
      <h2 class="text-lg font-semibold mb-4 text-text-primary dark:text-text-primary-dark">Choose an organisation</h2>
      <p class="text-sm text-text-secondary dark:text-text-secondary-dark mb-4">
        Your email is registered in multiple organisations with SSO enabled. Pick one to continue.
      </p>
      <ul class="space-y-2">
        <li v-for="t in ssoPicker" :key="t.tenant_id">
          <button
            type="button"
            class="btn-secondary w-full text-left"
            @click="redirectToSSO(t.tenant_id)"
          >
            {{ t.friendly_name }}
          </button>
        </li>
      </ul>
      <button class="text-primary dark:text-primary-light hover:underline text-sm mt-4 block mx-auto" @click="ssoPicker = []">Cancel</button>
    </div>

    <!-- Processing Token State -->
    <div v-if="isProcessingToken" class="card p-8 text-center">
      <div class="animate-spin h-8 w-8 mx-auto mb-4 text-primary dark:text-primary-light">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <p class="text-text-primary dark:text-text-primary-dark">Processing login token...</p>
    </div>

    <!-- Success Message for Email Login -->
    <div v-if="emailSent" class="card p-8 text-center">
      <div class="text-green-600 dark:text-green-400 mb-4">
        <svg class="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
        </svg>
      </div>
      <h3 class="text-lg font-semibold mb-2 text-text-primary dark:text-text-primary-dark">Check Your Email</h3>
      <p class="text-text-secondary dark:text-text-secondary-dark mb-4">
        We've sent a login token to <strong>{{ email }}</strong>
      </p>
      <p class="text-sm text-text-secondary dark:text-text-secondary-dark mb-6">
        Copy the token from your email and use it to sign in. The token will expire in 1 hour.
      </p>
      <div class="space-y-2">
        <button 
          @click="showTokenLogin = true; emailSent = false"
          class="text-primary dark:text-primary-light hover:underline text-sm block"
        >
          I have the token, let me sign in
        </button>
        <button 
          @click="resetEmailLogin"
          class="text-primary dark:text-primary-light hover:underline text-sm block"
        >
          Send another token or try a different email
        </button>
      </div>
    </div>

    <!-- Error Messages -->
    <p v-if="error" class="mt-4 text-error dark:text-error-dark text-sm bg-error-light dark:bg-error-dark/10 p-3 rounded">{{ error }}</p>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import { ErrorTypes, ErrorMessages } from '@/utils/errorHandler'

// Resolve the API base URL the same way the rest of the admin app does.
const getApiBase = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const clean = envUrl.replace(/\/$/, '')
  return clean.endsWith('/api/v1') ? clean : `${clean}/api/v1`
}

export default {
  name: 'LoginView',
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    
    // Form data
    const email = ref('')
    const passwordEmail = ref('')
    const password = ref('')
    const loginToken = ref('')
    
    // UI state
    const error = ref('')
    const isLoading = ref(false)
    const showPasswordLogin = ref(false)
    const showTokenLogin = ref(false)
    const emailSent = ref(false)
    const isProcessingToken = ref(false)

    // MFA stage machine — 'none' (default), 'code' (TOTP prompt for enrolled
    // users), 'enroll' (admin-required enrolment wizard).
    const mfaStage = ref('none')
    const mfaCode = ref('')
    const mfaSetup = ref(null) // { qr_code, secret, otpauth_url } from /mfa/setup
    const enrollmentToken = ref('') // scoped JWT from login response

    // OIDC SSO state. ssoPicker is non-empty when the lookup returns >1 tenant
    // and we need the user to disambiguate before redirecting to the IdP.
    const ssoPicker = ref([])

    const handleEmailLogin = async () => {
      try {
        isLoading.value = true
        error.value = ''
        await store.dispatch('loginWithEmail', email.value)
        // Auto-redirect to token input after sending email
        showTokenLogin.value = true
        emailSent.value = false
      } catch (err) {
        const errorType = err.response?.status === 400 ? ErrorTypes.VALIDATION : ErrorTypes.UNKNOWN
        error.value = ErrorMessages[errorType].login || 'Failed to send login email. Please try again.'
      } finally {
        isLoading.value = false
      }
    }

    const handlePasswordLogin = async () => {
      try {
        isLoading.value = true
        error.value = ''
        const data = await store.dispatch('login', {
          email: passwordEmail.value,
          password: password.value
        })
        if (data?.token) {
          router.push('/')
          return
        }
        if (data?.mfa_required) {
          mfaStage.value = 'code'
          mfaCode.value = ''
          return
        }
        if (data?.mfa_enrollment_required && data?.enrollment_token) {
          enrollmentToken.value = data.enrollment_token
          await startMfaEnrollment()
          return
        }
        error.value = 'Unexpected login response.'
      } catch (err) {
        const errorType = err.response?.status === 401 ? ErrorTypes.AUTHENTICATION : ErrorTypes.UNKNOWN
        error.value = ErrorMessages[errorType].login || 'Login failed. Please check your credentials.'
      } finally {
        isLoading.value = false
      }
    }

    const submitMfaCode = async () => {
      try {
        isLoading.value = true
        error.value = ''
        const data = await store.dispatch('login', {
          email: passwordEmail.value,
          password: password.value,
          mfa_code: mfaCode.value
        })
        if (data?.token) {
          mfaStage.value = 'none'
          mfaCode.value = ''
          router.push('/')
          return
        }
        error.value = 'Invalid code. Try again.'
      } catch (err) {
        error.value = err.response?.data?.error || 'Invalid code. Try again.'
      } finally {
        isLoading.value = false
      }
    }

    // Ask the backend to provision a TOTP secret + QR using the short-lived
    // enrolment token. We don't touch the store token because the enrolment
    // JWT can't do anything else — it's /mfa/setup + /mfa/confirm only.
    const startMfaEnrollment = async () => {
      try {
        isLoading.value = true
        const resp = await axios.post(
          `${getApiBase()}/admin/mfa/setup`,
          {},
          { headers: { Authorization: `Bearer ${enrollmentToken.value}` } }
        )
        mfaSetup.value = resp.data
        mfaStage.value = 'enroll'
        mfaCode.value = ''
      } catch (err) {
        error.value = err.response?.data?.error || 'Could not start 2FA setup. Please try signing in again.'
        mfaStage.value = 'none'
      } finally {
        isLoading.value = false
      }
    }

    const confirmMfaEnrollment = async () => {
      try {
        isLoading.value = true
        error.value = ''
        const resp = await axios.post(
          `${getApiBase()}/admin/mfa/confirm`,
          { code: mfaCode.value },
          { headers: { Authorization: `Bearer ${enrollmentToken.value}` } }
        )
        // Backend swaps the enrolment JWT for a full session JWT on success.
        if (resp.data?.token) {
          store.dispatch('setSessionFromToken', {
            token: resp.data.token,
            email: passwordEmail.value
          })
          mfaStage.value = 'none'
          router.push('/')
        } else {
          error.value = 'Setup succeeded but no session was issued. Please sign in again.'
          mfaStage.value = 'none'
        }
      } catch (err) {
        error.value = err.response?.data?.error || 'Invalid code. Try again.'
      } finally {
        isLoading.value = false
      }
    }

    const cancelMfa = () => {
      mfaStage.value = 'none'
      mfaCode.value = ''
      mfaSetup.value = null
      enrollmentToken.value = ''
    }

    // Look up which tenants have OIDC enabled for this email. If exactly one
    // matches, redirect straight to the IdP; otherwise show a picker. The
    // lookup endpoint does NOT reveal whether the email exists — it just
    // returns the (possibly empty) list of SSO-enabled tenants.
    const startSSO = async () => {
      if (!passwordEmail.value) {
        error.value = 'Enter your email first, then click "Sign in with SSO".'
        return
      }
      try {
        isLoading.value = true
        error.value = ''
        const resp = await axios.get(`${getApiBase()}/auth/oidc/lookup`, {
          params: { email: passwordEmail.value }
        })
        const tenants = resp.data?.tenants || []
        if (tenants.length === 0) {
          error.value = 'SSO is not configured for this account. Use password or email-token login.'
          return
        }
        if (tenants.length === 1) {
          redirectToSSO(tenants[0].tenant_id)
          return
        }
        ssoPicker.value = tenants
      } catch (err) {
        error.value = err.response?.data?.error || 'SSO lookup failed'
      } finally {
        isLoading.value = false
      }
    }

    const redirectToSSO = (tenantId) => {
      // Full-page redirect — the IdP will send the user back to
      // /auth/oidc/:tenant_id/callback, which in turn redirects to /login
      // with #oidc_token=... that onMounted below picks up.
      const base = getApiBase().replace(/\/api\/v1$/, '')
      window.location.href = `${base}/api/v1/auth/oidc/${tenantId}/login`
    }

    const handleTokenLogin = async () => {
      try {
        isProcessingToken.value = true
        error.value = ''
        await store.dispatch('loginWithToken', loginToken.value)
        router.push('/')
      } catch (err) {
        const errorType = err.response?.status === 404 ? ErrorTypes.NOT_FOUND : 
                         err.response?.status === 401 ? ErrorTypes.AUTHENTICATION : ErrorTypes.UNKNOWN
        error.value = ErrorMessages[errorType].login || 'Invalid or expired login token.'
        isProcessingToken.value = false
      }
    }

    const resetEmailLogin = () => {
      emailSent.value = false
      email.value = ''
      error.value = ''
    }

    // Auto-consume token from URL: either /login/:token (route param, e.g. from
    // invitation email CTAs) or /login?token=... (query string). This removes
    // the copy/paste friction after clicking an emailed login link.
    onMounted(async () => {
      // OIDC return trip: the callback handler redirects to /login#oidc_token=<jwt>
      // on success, or /login?oidc_error=<msg> on failure. Handle both.
      if (route.query.oidc_error) {
        error.value = String(route.query.oidc_error)
        // Strip the query so a refresh doesn't re-show it.
        router.replace({ path: route.path, query: {} })
      }
      const hash = window.location.hash || ''
      if (hash.startsWith('#oidc_token=')) {
        const tok = decodeURIComponent(hash.slice('#oidc_token='.length))
        if (tok) {
          store.dispatch('setSessionFromToken', { token: tok, email: '' })
          window.history.replaceState(null, '', window.location.pathname)
          router.push('/')
          return
        }
      }

      const urlToken = route.params.token || route.query.token
      if (!urlToken || typeof urlToken !== 'string') return
      loginToken.value = urlToken
      isProcessingToken.value = true
      try {
        await store.dispatch('loginWithToken', urlToken)
        router.push('/')
      } catch (err) {
        const errorType = err.response?.status === 404 ? ErrorTypes.NOT_FOUND :
                         err.response?.status === 401 ? ErrorTypes.AUTHENTICATION : ErrorTypes.UNKNOWN
        error.value = ErrorMessages[errorType]?.login || 'Invalid or expired login token. Please request a new one.'
        isProcessingToken.value = false
        showTokenLogin.value = true
      }
    })

    return {
      email,
      passwordEmail,
      password,
      loginToken,
      error,
      isLoading,
      showPasswordLogin,
      showTokenLogin,
      emailSent,
      isProcessingToken,
      handleEmailLogin,
      handlePasswordLogin,
      handleTokenLogin,
      resetEmailLogin,
      mfaStage,
      mfaCode,
      mfaSetup,
      submitMfaCode,
      confirmMfaEnrollment,
      cancelMfa,
      ssoPicker,
      startSSO,
      redirectToSSO
    }
  }
}
</script>